import * as Yup from 'yup';

import {
  Button,
  Form,
  InputField,
  PasswordField,
  SelectField,
  SelectOption
} from '@vmware-react/clarity';
import React, { useCallback, useRef } from 'react';

import { AsyncOpState } from 'vrlcm-gui-types';
import { l10n } from '../i18n/i18nUtil';

export interface LoginFormData {
  userType: 'local' | 'vidm';
  username: string;
  password: string;
}

const isUserOnIpAddress = () => {
  const parts = window.location.hostname.split('.');
  return parts.length === 4 && parts.every(p => !!p.match(/^[0-9]+$/));
};

interface Props {
  submitState: AsyncOpState;
  vidmOptionEnabled: boolean;
  onSubmit: (formData: LoginFormData) => void;
  submitErrorMessage?: string;
  onChange: (formData: LoginFormData) => void;
  vcfMode: boolean;
  vcfDebugMode: boolean;
}

const initialLoginFormData: LoginFormData = {
  password: '',
  username: '',
  userType: 'local'
};

const LOGIN_VIA_VIDM_URL = '/lcm/login/vidm';

const validationSchema = (vcfMode: boolean, vcfDebugMode: boolean) => {
  const isLocalAdminDisabled = vcfMode && !vcfDebugMode;
  return Yup.object().shape({
    userType: Yup.string().required(
      l10n('components.withAppRoot.userTypeRequired')
    ),
    username: Yup.string().when('userType', {
      is: 'local',
      then: Yup.string()
        .required(l10n('components.withAppRoot.userNameRequired'))
        .notOneOf(
          isLocalAdminDisabled ? ['admin@local'] : [],
          l10n('components.withAppRoot.accessDisabled')
        )
    }),
    password: Yup.string().when('userType', {
      is: 'local',
      then: Yup.string().required(
        l10n('components.withAppRoot.passwordRequired')
      )
    })
  });
};

/**
 * State full and uncontrolled login form component.
 */
const LoginForm: React.FC<Props> = (props: Props) => {
  const {
    vidmOptionEnabled,
    submitState,
    onSubmit,
    submitErrorMessage,
    onChange,
    vcfMode,
    vcfDebugMode
  } = props;
  const initialValid = validationSchema(vcfMode, vcfDebugMode).isValidSync(
    initialLoginFormData
  );
  const validityRef = useRef<boolean>(initialValid);
  const dataRef = useRef<LoginFormData>(initialLoginFormData);

  const loginBtnDisabled = submitState === AsyncOpState.Inprogress;

  const handleSubmit = useCallback(() => onSubmit(dataRef.current), [onSubmit]);

  const instantDataChangeUpdate = useCallback(() => {
    onChange(dataRef.current);
  }, [onChange]);

  const loginWithVidmDisabled = isUserOnIpAddress();

  return (
    <Form
      type="horizontal"
      autoComplete="off"
      initialValues={initialLoginFormData}
      validationSchema={validationSchema(vcfMode, vcfDebugMode)}
      enableReinitialize={true}
      isInitialValid={initialValid}
      onSubmit={handleSubmit}
      hideReqMsg={true}
      reqMsg={l10n('requiredFields')}
    >
      {formProps => {
        const { values, isValid, errors, touched } = formProps;
        validityRef.current = isValid;
        dataRef.current = values as LoginFormData;
        return (
          <>
            <SelectField
              name="userType"
              error={false}
              onChange={instantDataChangeUpdate}
            >
              <SelectOption value="local">
                {l10n('components.withAppRoot.localUser')}
              </SelectOption>
              {vidmOptionEnabled && (
                <SelectOption value="vidm">
                  {l10n('components.withAppRoot.idmUser')}
                </SelectOption>
              )}
            </SelectField>
            {values.userType === 'local' && (
              <>
                <InputField
                  name="username"
                  placeholder={l10n('components.withAppRoot.usernameExample', [
                    vcfMode ? 'vcfadmin@local' : 'admin@local'
                  ])}
                  // @ts-ignore
                  error={
                    touched && touched.username && errors && errors.username
                  }
                  // @ts-ignore
                  errorMessage={errors && errors.username}
                  onChange={instantDataChangeUpdate}
                />
                <PasswordField
                  name="password"
                  placeholder={l10n('password')}
                  // @ts-ignore
                  error={
                    touched && touched.password && errors && errors.password
                  }
                  // @ts-ignore
                  errorMessage={errors && errors.password}
                  onChange={instantDataChangeUpdate}
                />
              </>
            )}
            {submitState === AsyncOpState.Failed && (
              <div className="error active">{submitErrorMessage}</div>
            )}
            {values.userType !== 'local' && loginWithVidmDisabled && (
              <div className="error active">
                {l10n('components.withAppRoot.loginWithVidmDisabled')}
              </div>
            )}
            {values.userType === 'local' ? (
              <Button
                block={true}
                type="submit"
                class="primary"
                disabled={loginBtnDisabled || !isValid}
              >
                {l10n('login')}
              </Button>
            ) : (
              <>
                {!loginWithVidmDisabled ? (
                  <a href={LOGIN_VIA_VIDM_URL}>
                    <Button block={true} class="primary">
                      {l10n('components.withAppRoot.loginWithVidm')}
                    </Button>
                  </a>
                ) : (
                  <Button block={true} class="primary" disabled={true}>
                    {l10n('components.withAppRoot.loginWithVidm')}
                  </Button>
                )}
              </>
            )}
          </>
        );
      }}
    </Form>
  );
};

export default LoginForm;
